import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { useTranslation } from "react-i18next";
import { FormCommonProps } from "../../../../common/forms/types";
import { AsyncFetchRender } from "../../../../common/async/AsyncFetchRender.tsx";
import { SitesApi } from "src/api/generated/erp/common/sites/sitesApi";
import { Site } from "src/api/generated/erp/db/types/tables/site";
import { FormTextField } from "src/components/common/forms/fields/FormTextField";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox";
import { enumOptions } from "src/components/common/forms/fields/selectionFieldUtils";
import { spanGridColumns, startNewRow } from "src/components/common/forms/styles";
import { Company } from "src/api/generated/erp/db/types/tables/company";
import { ObjectAttributeField } from "src/api/generated/erp/common/objectAttributes/objectAttributeField";
import { getSiteCertificateTypeLabels } from "src/api/generated/erp/db/types/enums/siteCertificateType";
import { DeepPartial, useFieldArray, useForm } from "react-hook-form";
import { ObjectAttribute } from "src/api/generated/erp/db/types/tables/objectAttribute";
import { requiredNonNegativeIntegerRule, requireRule } from "src/components/common/forms/validation";
import { ObjectAttributeFieldComponent } from "../objectAttributes/ObjectAttributeFieldComponent";
import { FormSection } from "src/components/common/forms/FormSection";
import { getPurchaseOrderLineAppendModeLabels } from "src/api/generated/erp/db/types/enums/purchaseOrderLineAppendMode";

export interface SiteFormProps extends FormCommonProps<number> {
	siteId?: number;
}

export const SiteForm = ({ siteId, ...other }: SiteFormProps) => {
	return (
		<AsyncFetchRender
			fetch={() => SitesApi.getSiteFormData({ siteId })}
			content={(siteFormData) => {
				const defaultValues =
					siteFormData.site !== null
						? {
							...siteFormData.site,
							attributes: siteFormData.attributeValues
						}
						: FORM_DEFAULT_VALUES;

				const defaultValuesWithAttrs = setAttributeValues(
					defaultValues,
					siteFormData.attributeFields
				);

				return (
					<SiteFormContent
						companyOptions={siteFormData.companyOptions}
						attributeFields={siteFormData.attributeFields}
						defaultValues={defaultValuesWithAttrs}
						{...other}
					/>
				);
			}}
		/>
	);
};

interface SiteFormContentProps extends FormCommonProps<number> {
	defaultValues: DeepPartial<SiteWithAttributes>;
	companyOptions: Company[];
	attributeFields: ObjectAttributeField[];
}

interface SiteWithAttributes extends Site {
	attributes: ObjectAttribute[];
}

const SiteFormContent = ({
							 defaultValues,
							 companyOptions,
							 attributeFields,
							 onFormEdited,
							 ...other
						 }: SiteFormContentProps) => {
	const { t } = useTranslation();
	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch
	} = useForm<SiteWithAttributes>({
		mode: "all",
		defaultValues: defaultValues
	});

	if (onFormEdited) watch(onFormEdited);

	const attributeFieldArray = useFieldArray({
		control,
		name: "attributes"
	});

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={submitForm}
			columns={2}
			{...other}
		>
			<FormTextField
				control={control}
				name={"siteName"}
				label={t("site")}
				rules={requireRule()}
				autoFocus
			/>
			<FormTextField control={control} name={"description"} label={t("description")} />
			<FormTextField control={control} name={"address_1"} label={t("address_1")} />
			<FormTextField control={control} name={"address_2"} label={t("address_2")} />
			<FormTextField control={control} name={"postalCode"} label={t("postal_code")} />
			<FormTextField control={control} name={"city"} label={t("city")} />
			<FormSelectField
				control={control}
				name={"companyId"}
				options={companyOptions.map((c) => ({
					key: c.companyId,
					label: c.companyName
				}))}
				label={t("company")}
				rules={requireRule()}
			/>
			<FormSection label={t("parameters")}>
				<FormTextField
					control={control}
					name={"pickingDurationDays"}
					label={t("picking_duration_days")}
					type={"number"}
					rules={requiredNonNegativeIntegerRule()}
				/>
				<FormTextField
					control={control}
					name={"shopOrderLineObjectAdvanceDays"}
					label={t("shop_order_bom_acquisition_advance_days")}
					rules={requiredNonNegativeIntegerRule()}
				/>
				<FormSelectField
					control={control}
					name={"certificateType"}
					label={t("certificate_type")}
					options={enumOptions(getSiteCertificateTypeLabels())}
					sx={startNewRow}
				/>
				<FormTextField
					control={control}
					name={"certificateNo"}
					label={t("certificate_number")}
				/>
				<FormCheckbox
					control={control}
					name={"showFullOperationProductionBom"}
					label={t("show_full_operation_production_bom")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"projectAutomaticClose"}
					label={t("close_projects_automatically")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"subProjectAutomaticClose"}
					label={t("close_sub_projects_automatically")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"shopOrderAutomaticReservation"}
					label={t("shop_order_automatic_reservation")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"shopOrderAutomaticPicking"}
					label={t("shop_order_automatic_picking")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"updateCustomerOrderLineCostWhenPicking"}
					label={t("update_customer_order_line_cost_when_picking")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"inventoryLevelEnabledAsDefault"}
					label={t("inventory_level_enabled_as_default")}
					sx={spanGridColumns}
				/>
				<FormCheckbox
					control={control}
					name={"enableNegativeInventoryLevel"}
					label={t("enable_negative_inventory_level")}
					sx={spanGridColumns}
				/>
				<FormSelectField
					control={control}
					name={"customerOrderPurchaseOrderLineAppendMode"}
					label={t("purchase_order_from_customer_order_line_append_mode")}
					options={enumOptions(getPurchaseOrderLineAppendModeLabels())}
					sx={spanGridColumns}
				/>
				<FormSelectField
					control={control}
					name={"shopOrderPurchaseOrderLineAppendMode"}
					label={t("purchase_order_from_shop_order_line_append_mode")}
					options={enumOptions(getPurchaseOrderLineAppendModeLabels())}
					sx={spanGridColumns}
				/>
				<FormSelectField
					control={control}
					name={"projectPurchaseOrderLineAppendMode"}
					label={t("purchase_order_from_project_append_mode")}
					options={enumOptions(getPurchaseOrderLineAppendModeLabels())}
					sx={spanGridColumns}
				/>

				{attributeFieldArray.fields.map((field, index) => {
					const attributeField = attributeFields.find(
						(f) => f.id === field.attributeFieldId
					)!;
					return (
						<ObjectAttributeFieldComponent
							key={field.id}
							control={control}
							name={`attributes.${index}.attributeValue`}
							objectAttributeField={attributeField}
						/>
					);
				})}
				<FormTextField
					control={control}
					name={"consignmentNoteRepText"}
					label={t("consignment_note_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
				<FormTextField
					control={control}
					name={"purchaseOrderRepText"}
					label={t("purchase_order_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
				<FormTextField
					control={control}
					name={"deliveryNoteRepText"}
					label={t("delivery_note_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
				<FormTextField
					control={control}
					name={"invoiceRepText"}
					label={t("invoice_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
				<FormTextField
					control={control}
					name={"customerOrderOfferRepText"}
					label={t("customer_order_offer_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
				<FormTextField
					control={control}
					name={"customerOrderConfirmationRepText"}
					label={t("customer_order_confirmation_print_default_text")}
					multiline
					sx={spanGridColumns}
				/>
			</FormSection>
		</AavoForm>
	);
};

const FORM_DEFAULT_VALUES: DeepPartial<SiteWithAttributes> = {
	timeZone: "Europe/Helsinki",
	pickingDurationDays: 0,
	shopOrderLineObjectAdvanceDays: 0,
	showFullOperationProductionBom: false,
	projectAutomaticClose: false,
	subProjectAutomaticClose: false,
	shopOrderAutomaticPicking: true,
	shopOrderAutomaticReservation: true,
	attributes: []
};

const setAttributeValues = (
	site: DeepPartial<SiteWithAttributes>,
	attributeFields: ObjectAttributeField[]
): DeepPartial<SiteWithAttributes> => {
	const attributeValues = attributeFields.map((f) => {
		const existingValue = site.attributes?.find((a) => a?.attributeFieldId === f.id);
		return existingValue ?? { attributeFieldId: f.id, value: null };
	});
	return {
		...site,
		attributes: attributeValues
	};
};

const submitForm = async (site: SiteWithAttributes): Promise<number> => {
	const result = await SitesApi.save({
		site: site,
		attributes: site.attributes
	});
	return result.siteId;
};
